import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

export default function WizardErrorCorrectionPreviewForm({ feedbackSourceData, feedbackTargetData, principleData, isCompact = false }) {
    const getImageSrc = (imageData, fileName) => {
        if (!imageData) return null;

        // If it's already a full URL (starts with http or https), return it as is
        if (typeof imageData === 'string' && (imageData.startsWith('http://') || imageData.startsWith('https://'))) {
            return imageData;
        }

        // If it's already a data URL, return it as is
        if (typeof imageData === 'string' && imageData.startsWith('data:')) {
            return imageData;
        }

        // If it starts with /, check if it's a valid storage path or a base64 string
        if (typeof imageData === 'string' && imageData.startsWith('/')) {
            // Check if it looks like a storage path (contains 'storage' or 'posters')
            if (imageData.includes('/storage/') || imageData.includes('/posters/') || imageData.startsWith('/storage') || imageData.startsWith('/posters')) {
                return imageData;
            }
            // If it starts with / but doesn't look like a path, it might be base64
            // Base64 strings that start with alphanumeric chars might have been prefixed with /
            // Check if the rest looks like base64 (alphanumeric, +, /, =)
            const withoutSlash = imageData.substring(1);
            const base64Pattern = /^[A-Za-z0-9+/=]+$/;
            if (base64Pattern.test(withoutSlash) && withoutSlash.length > 50) {
                // Looks like base64, remove the / and treat as base64
                imageData = withoutSlash;
            } else {
                // Keep as relative URL path
                return imageData;
            }
        }

        // At this point, treat it as base64 string and construct data URL
        try {
            // Determine MIME type from fileName or default
            let mimeType = 'image/jpeg'; // default
            if (fileName) {
                const fileExtension = fileName.split('.').pop()?.toLowerCase();
                if (fileExtension === 'pdf') {
                    mimeType = 'application/pdf';
                } else if (['jpg', 'jpeg'].includes(fileExtension)) {
                    mimeType = 'image/jpeg';
                } else if (fileExtension === 'png') {
                    mimeType = 'image/png';
                }
            }

            // Construct data URL
            return `data:${mimeType};base64,${imageData}`;
        } catch (e) {
            console.error('Error processing image:', e, 'Image data length:', imageData?.length);
            return null;
        }
    };

    // Get image from either 'image' or 'image_path' property (for saved posters)
    const sourceImageData = feedbackSourceData?.image || feedbackSourceData?.image_path;
    const targetImageData = feedbackTargetData?.image || feedbackTargetData?.image_path;

    const sourceImageSrc = getImageSrc(sourceImageData, feedbackSourceData?.fileName);
    const targetImageSrc = getImageSrc(targetImageData, feedbackTargetData?.fileName);
    const sourceIsPdf = feedbackSourceData?.fileName?.toLowerCase()?.endsWith('.pdf');
    const targetIsPdf = feedbackTargetData?.fileName?.toLowerCase()?.endsWith('.pdf');

    const sourceText = feedbackSourceData?.text || 'Communication with error';
    const targetText = feedbackTargetData?.text || 'Text goes here';
    const principleText = principleData?.selectedPrinciple || 'ECF';

    const flowchartClass = isCompact ? 'error-correction-flowchart error-correction-flowchart-compact' : 'error-correction-flowchart';
    const containerClass = isCompact ? 'wizard-error-correction-preview-form wizard-error-correction-preview-form-compact' : 'wizard-error-correction-preview-form';

    return (
        <div className={containerClass}>
            <div className={flowchartClass}>
                {/* SVG Arrows Overlay removed from top level */}

                {/* Top Section with Columns */}
                <div className="ecf-top-section">
                    {/* Left Column: Source Box */}
                    <div className="ecf-column">
                        <div className="ecf-top-box ecf-source-box">
                            <div className="ecf-box-content">
                                <span className="ecf-box-text">{sourceText}</span>
                                {sourceImageSrc && !sourceIsPdf ? (
                                    <img
                                        src={sourceImageSrc}
                                        alt="Source"
                                        className="ecf-profile-picture"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            const placeholder = e.target.parentElement.querySelector('.ecf-profile-placeholder-fallback');
                                            if (placeholder) {
                                                placeholder.style.display = 'flex';
                                            }
                                        }}
                                    />
                                ) : null}
                                {sourceImageSrc && !sourceIsPdf && (
                                    <div className="ecf-profile-placeholder ecf-profile-placeholder-fallback" style={{ display: 'none' }}>
                                        <span>?</span>
                                    </div>
                                )}
                                {(!sourceImageSrc || sourceIsPdf) && (
                                    <div className="ecf-profile-placeholder">
                                        <span>{sourceIsPdf ? 'PDF' : '?'}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* <div className="ecf-arrow-down ecf-arrow-source">
                            <svg className="ecf-arrow-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <defs>
                                    <marker id="arrowhead-source" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                        <polygon points="0 0, 10 3.5, 0 7" fill="#5A5A5A" />
                                    </marker>
                                </defs>
                                <path d="M 50 0 L 50 50 L 90 90" stroke="#5A5A5A" strokeWidth="2" fill="none" markerEnd="url(#arrowhead-source)" />
                            </svg>
                            <div className="ecf-dot ecf-dot-red"></div>
                        </div> */}
                    </div>

                    {/* Right Column: Target Box + Arrow */}
                    <div className="ecf-column">
                        <div className="ecf-top-box ecf-target-box">
                            <div className="ecf-box-content">
                                <span className="ecf-box-text">{targetText}</span>
                                {targetImageSrc && !targetIsPdf ? (
                                    <img
                                        src={targetImageSrc}
                                        alt="Target"
                                        className="ecf-profile-picture"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            const placeholder = e.target.parentElement.querySelector('.ecf-profile-placeholder-fallback');
                                            if (placeholder) {
                                                placeholder.style.display = 'flex';
                                            }
                                        }}
                                    />
                                ) : null}
                                {targetImageSrc && !targetIsPdf && (
                                    <div className="ecf-profile-placeholder ecf-profile-placeholder-fallback" style={{ display: 'none' }}>
                                        <span>?</span>
                                    </div>
                                )}
                                {(!targetImageSrc || targetIsPdf) && (
                                    <div className="ecf-profile-placeholder">
                                        <span>{targetIsPdf ? 'PDF' : '?'}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* <div className="ecf-arrow-down ecf-arrow-target">
                            <svg className="ecf-arrow-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <defs>
                                    <marker id="arrowhead-target" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                        <polygon points="0 0, 10 3.5, 0 7" fill="#5A5A5A" />
                                    </marker>
                                </defs>
                                <path d="M 50 0 L 50 50 L 10 90" stroke="#5A5A5A" strokeWidth="2" fill="none" markerEnd="url(#arrowhead-target)" />
                            </svg>
                            <div className="ecf-dot ecf-dot-blue"></div>
                        </div> */}
                    </div>
                </div>

                {/* Central Diamond with Principle and Feedback Labels */}
                <div className="ecf-center-section">
                    {/* SVG Arrows Overlay - Centered on Diamond */}
                    <div className="ecf-arrows-overlay">
                        <svg viewBox="-400 -200 800 400" className="ecf-arrows-svg">
                            <defs>
                                <marker id="arrowhead-gray" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                    <polygon points="0 0, 10 3.5, 0 7" fill="#5A5A5A" />
                                </marker>
                            </defs>

                            {/* Arrow 1: ECF Left -> Source Left (Feedback Loop) */}
                            {/* Path: ECF Left (-85, 0) -> Left -> Up -> Right -> Source Left (-118, -320) */}
                            <path
                                d="M -85 0 L -320 0 L -320 -320 L -118 -320"
                                stroke="#5A5A5A"
                                strokeWidth="1"
                                fill="none"
                                markerEnd="url(#arrowhead-gray)"
                            />
                            <circle cx="-310" cy="-160" r="5" fill="#22A6FF" />

                            {/* Arrow 2: Source Bottom -> ECF Top (Input) */}
                            {/* Path: Source Bottom (0, -276) -> ECF Top (0, -86) */}
                            {/* Vertical straight line */}
                            <path
                                d="M 0 -276 L 0 -86"
                                stroke="#5A5A5A"
                                strokeWidth="1"
                                fill="none"
                                markerEnd="url(#arrowhead-gray)"
                            />
                            <circle cx="20" cy="-266" r="5" fill="#FF4444" />

                            {/* Arrow 3: Target Bottom -> ECF Right (Feedback 2) */}
                            {/* Path: Target Bottom (240, -276) -> Down -> Left -> ECF Right (86, 0) */}
                            <path
                                d="M 240 -276 L 240 0 L 86 0"
                                stroke="#5A5A5A"
                                strokeWidth="1"
                                fill="none"
                                markerEnd="url(#arrowhead-gray)"
                            />
                            <circle cx="230" cy="-136" r="5" fill="#22A6FF" />
                        </svg>

                        {/* Feedback Labels */}
                        <div className="ecf-feedback-text-left" style={{ position: 'absolute', left: '-250px', top: '-30px' }}>
                            <ChatBubbleOutlineIcon style={{ fontSize: 16, color: '#000', marginRight: 6 }} />
                            <span>Feedback</span>
                        </div>
                        <div className="ecf-feedback-text-right" style={{ position: 'absolute', left: '120px', top: '-30px' }}>
                            <ChatBubbleOutlineIcon style={{ fontSize: 16, color: '#000', marginRight: 6 }} />
                            <span>Feedback</span>
                        </div>
                    </div>

                    {/* Left Feedback Label (Removed old) */}
                    {/* <div className="ecf-feedback-label ecf-feedback-label-left"> ... </div> */}

                    {/* Central Diamond */}
                    <div className="ecf-diamond">
                        <div className="ecf-diamond-content">
                            <span className="ecf-diamond-text">{principleText}</span>
                            <EditIcon className="ecf-pencil-icon" />
                        </div>
                    </div>

                    {/* Right Feedback Label (Removed old) */}
                    {/* <div className="ecf-feedback-label ecf-feedback-label-right"> ... </div> */}
                </div>

                {/* Horizontal Feedback Arrows from Diamond */}
                <div className="ecf-feedback-arrows">
                    {/* Left Feedback Arrow */}
                    {/* <div className="ecf-feedback-arrow-container ecf-feedback-left">
                        <svg className="ecf-horizontal-arrow" viewBox="0 0 200 50" preserveAspectRatio="none">
                            <path d="M 0 25 L 180 25 M 175 20 L 180 25 L 175 30" stroke="#5A5A5A" strokeWidth="2" fill="none" />
                        </svg>
                        <div className="ecf-dot ecf-dot-blue"></div>
                    </div> */}

                    {/* Right Feedback Arrow */}
                    {/* <div className="ecf-feedback-arrow-container ecf-feedback-right">
                        <svg className="ecf-horizontal-arrow" viewBox="0 0 200 50" preserveAspectRatio="none">
                            <path d="M 200 25 L 20 25 M 25 20 L 20 25 L 25 30" stroke="#5A5A5A" strokeWidth="2" fill="none" />
                        </svg>
                        <div className="ecf-dot ecf-dot-blue"></div>
                    </div> */}
                </div>

                {/* Looping Paths - Curved arrows from feedback blue dots back to top boxes */}
                {/* <div className="ecf-loop-paths"> */}
                {/* Left loop path - from blue dot curving up to source box */}
                {/* <svg className="ecf-loop-svg-left" viewBox="0 0 400 320" preserveAspectRatio="none">
                        <defs>
                            <marker id="arrowhead-left-loop" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                                <polygon points="0 0, 10 3, 0 6" fill="#5A5A5A" />
                            </marker>
                        </defs>
                        <path
                            d="M 10 300 Q 50 250 90 200 Q 130 150 170 100 Q 210 50 250 20 Q 280 5 320 0"
                            stroke="#5A5A5A"
                            strokeWidth="2"
                            fill="none"
                            markerEnd="url(#arrowhead-left-loop)"
                        />
                    </svg> */}

                {/* Right loop path - from blue dot curving up to target box */}
                {/* <svg className="ecf-loop-svg-right" viewBox="0 0 400 320" preserveAspectRatio="none">
                        <defs>
                            <marker id="arrowhead-right-loop" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                                <polygon points="0 0, 10 3, 0 6" fill="#5A5A5A" />
                            </marker>
                        </defs>
                        <path
                            d="M 390 300 Q 350 250 310 200 Q 270 150 230 100 Q 190 50 150 20 Q 120 5 80 0"
                            stroke="#5A5A5A"
                            strokeWidth="2"
                            fill="none"
                            markerEnd="url(#arrowhead-right-loop)"
                        />
                    </svg> */}
                {/* </div> */}

                {/* Arrow to Final Box */}
                <div className="ecf-arrow-to-final">
                    <svg className="ecf-arrow-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M 50 0 L 50 100 L 45 95 M 50 100 L 55 95" stroke="#5A5A5A" strokeWidth="1" fill="none" />
                    </svg>
                    <div className="ecf-dot ecf-dot-green"></div>
                </div>

                {/* Final Output Box */}
                <div className="ecf-final-box">
                    <div className="ecf-final-box-content">
                        <span className="ecf-final-box-text">Communication or Application</span>
                        <EditIcon className="ecf-pencil-icon" />
                    </div>
                </div>
            </div>
        </div>
    );
}

